// Implements: F-MCK-013, F-PRV-015
/**
 * The gallery thumbnail as a template (owner decision 2026-10-14). Pure.
 *
 * Nothing renders the mockup itself: the card draws the v3 generic mockup
 * wireframe from what `GET /mockups` already carries per card. The title is
 * the listing's (`Mockup NN` for an unnamed mockup, decided by the server), and
 * the accent is the project's primary (the palette's `band`) once the mockup
 * has generated. Before that `accent` is `null` and the wireframe takes the
 * neutral tint of the design-system tokens; the theme aliases do the rest, so
 * both the light and the dark theme apply.
 *
 * Every colour is checked again here: the accent reaches a CSS custom property,
 * so only a strict `#rrggbb` passes, and one bad colour in the palette makes
 * the whole thumbnail neutral. The title is kept whole: the thumbnail cuts it
 * on screen with an ellipsis and its accessible label carries it in full.
 */

import type { MockupSummary, ThumbnailPalette } from "../../../types/api";

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

export interface ThumbnailTemplate {
  readonly title: string;
  /** The project's primary (`#rrggbb`), or `null` for the neutral tint of the tokens. */
  readonly accent: string | null;
}

function isColour(value: unknown): value is string {
  return typeof value === "string" && HEX_COLOUR.test(value);
}

function safeAccent(palette: ThumbnailPalette | null | undefined): string | null {
  if (palette === null || palette === undefined) {
    return null;
  }
  const { background, band, text } = palette;
  return isColour(background) && isColour(band) && isColour(text) ? band : null;
}

/** The template a card draws, from the listing's thumbnail or from the name alone. */
export function thumbnailTemplateOf(mockup: MockupSummary): ThumbnailTemplate {
  const title = mockup.thumbnail?.title ?? mockup.name;
  return { title, accent: safeAccent(mockup.thumbnail?.palette) };
}
